import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CompareHelper, DotNetBridge, SettingsCatalogueKey } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { from, Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { LocalSettingsService } from '../../../../local-settings.service';
import { ProfileSelectionComponentInfo } from '../profile-selection/profile-selection-component-info';
import { IProfileSettings, ProfileSetting, ProfileSettings } from './../models/profile-settings.model';
import { LoginSettingsService } from './login-settings.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileSettingsService extends LocalSettingsService<IProfileSettings, ProfileSettings, ProfileSetting> {
  constructor(
    abcConfigService: AbcConfigService,
    private router: Router,
    private loginSettingsService: LoginSettingsService
  ) {
    super(abcConfigService, ProfileSettings, ProfileSetting);
  }

  public static readonly ANONYMOUS_USER_NAME: string = '__ANONYMOUS__';
  public static readonly HTTP_HEADER_PROFILE_ID: string = 'ProfileId';

  protected get settingsCatalogueStorageKeyName(): string {
    return 'ProfileSettings';
  }

  protected override buildSettingsCatalogueKey(): Observable<SettingsCatalogueKey> {
    return this.loginSettingsService.getCurrentLoginSettings().pipe(
      map((loginSettings) =>
        loginSettings
          ? {
              WebApi: this.abcConfigService.configuration.WebApi.BaseURL,
              UserName: loginSettings.userName ?? ProfileSettingsService.ANONYMOUS_USER_NAME, // Value must not be empty!
            }
          : null
      )
    );
  }

  public getCurrentProfileSettings(): Observable<IProfileSettings> {
    if (!ProfileSelectionComponentInfo.areProfilesSupported(this.router)) {
      return of(null);
    }
    if (DotNetBridge.hasContext) {
      return from(this.getDotNetContextProfileSettings());
    } else {
      const settings = this.getQueryParamsProfileSettings();
      if (settings) {
        return of(settings);
      }
      return this.readSettingsFromStorage();
    }
  }

  public getCurrentProfileSettingsWithContext(contextIdentifier: string): Observable<IProfileSettings> {
    return this.getCurrentProfileSettings().pipe(
      map((settings) => {
        if (settings) {
          const copy = new ProfileSettings(settings);
          copy.contextIdentifier = contextIdentifier;
          return copy;
        } else {
          return settings;
        }
      })
    );
  }

  private async getDotNetContextProfileSettings(): Promise<IProfileSettings> {
    const s = new ProfileSettings();
    s.id = await DotNetBridge.context.get('ProfileId');
    return s;
  }

  static readonly CurrentProfileIdKey = 'currentProfileId';
  private getQueryParamsProfileSettings(): IProfileSettings {
    try {
      const url = new URL(window.location?.href);
      let profileId = url.searchParams.get('profileId');
      if (!profileId) {
        profileId = url.searchParams.get('profile_id');
      }
      if (profileId) {
        sessionStorage.setItem(ProfileSettingsService.CurrentProfileIdKey, profileId);
      } else {
        profileId = sessionStorage.getItem(ProfileSettingsService.CurrentProfileIdKey);
      }
      if (profileId) {
        const s = new ProfileSettings();
        s.id = profileId;
        return s;
      }
    } catch (err) {
      console.error(err);
    }
    return undefined as IProfileSettings;
  }

  public setCurrentProfileSettings(profileSettings: IProfileSettings): Observable<void> {
    return this.writeSettingsToStorage(profileSettings);
  }

  protected override writeSettingsToStorage(
    settings: IProfileSettings,
    specificStorageOnly?: Storage
  ): Observable<void> {
    sessionStorage.removeItem(ProfileSettingsService.CurrentProfileIdKey);
    return super.writeSettingsToStorage(settings, specificStorageOnly);
  }

  public removeCurrentProfileSettings(): Observable<void> {
    return this.setCurrentProfileSettings(null);
  }

  public get hasCurrentProfileSettings(): Observable<boolean> {
    return this.getCurrentProfileSettings().pipe(map((profileSettings) => profileSettings != null));
  }

  public updateCurrentProfileSettings(profileSettings: IProfileSettings): Observable<void> {
    return this.readSettingsFromStorage(sessionStorage).pipe(
      concatMap((oldSessionSettings) =>
        this.readSettingsFromStorage(localStorage).pipe(
          concatMap((oldLocalSettings) => {
            if (CompareHelper.areDeepEqual(oldSessionSettings, oldLocalSettings)) {
              return this.writeSettingsToStorage(profileSettings);
            } else {
              return this.writeSettingsToStorage(profileSettings, sessionStorage);
            }
          })
        )
      )
    );
  }

  public hasProfileNotFoundMessage(err: any): boolean {
    return ProfileSelectionComponentInfo.hasProfileNotFoundMessage(err);
  }

  public buildProfileKey(settings?: IProfileSettings): Observable<string> {
    return this.buildSettingsCatalogueKey().pipe(
      concatMap((k) => {
        if (settings) {
          return of({ key: k, settings: settings });
        } else {
          return this.getCurrentProfileSettings().pipe(
            map((_) => {
              return { key: k, settings: _ };
            })
          );
        }
      }),
      map((_) => {
        const k = _.key;
        const p = _.settings;
        const api = k?.WebApi;
        const userName = k?.UserName;
        const profileId = p?.id;
        const contextIdentifier = p?.contextIdentifier;
        const arr = [];
        if (userName) {
          arr.push('user=' + userName);
        }
        if (profileId) {
          arr.push('profile=' + profileId);
        }
        if (contextIdentifier) {
          arr.push('context=' + contextIdentifier);
        }
        if (api && window?.location?.href?.indexOf(api) != 0) {
          arr.push('api=' + api);
        }
        return arr.join('&');
      })
    );
  }
}
